import React, { useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { InputText, ErrorField } from '../../lib/HooksFormFields';
import { createSalesPointAction } from '../../actions/salesPoints';
import { useAppDispatch } from '../../reducers';
import { SET_TOAST } from '../../actions/types';
import Loader from '../Loader';
import styles from './CreateProspectModal.module.scss';

interface DuplicateSalesPoint {
  _id: string;
  name: string;
  customerStatus: string;
  location: {
    address1: string;
    postalCode: string;
    city: string;
  };
}

const CreateProspectModal = ({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [duplicates, setDuplicates] = useState<DuplicateSalesPoint[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FieldValues>({
    defaultValues: {
      name: '',
      siret: '',
      location: {
        address1: '',
        postalCode: '',
        city: '',
      },
    },
  });

  const siretValue = watch('siret');
  const sirenValue = siretValue?.length >= 9 ? siretValue.substring(0, 9) : '';

  async function onSubmit(values: any) {
    const payload = {
      name: values.name,
      siret: values.siret,
      location: {
        address1: values?.location?.address1,
        postalCode: values?.location?.postalCode,
        city: values?.location?.city,
      },
    };
    setIsLoading(true);

    const response = await createSalesPointAction(dispatch, payload);

    setIsLoading(false);

    if (response?.data?.created) {
      dispatch({
        type: SET_TOAST,
        payload: { type: 'success', message: 'Prospect créé avec succès' },
      });
      onCreated();
      onClose();
      return;
    }

    if (response?.data?.duplicates) {
      setDuplicates(response.data.duplicates);
    }
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loader}>
          <Loader />
        </div>
      </div>
    );
  }

  if (duplicates) {
    return (
      <div className={styles.container}>
        <h2>Point de vente existant</h2>
        <p className={styles.subtitle}>
          Un ou plusieurs points de vente existent déjà avec ce SIRET.
        </p>
        <div className={styles.duplicates}>
          {duplicates.map((d) => (
            <button
              key={d._id}
              type="button"
              className={styles.duplicate}
              onClick={() => {
                onClose();
                navigate(`/client/${d._id}`);
              }}
            >
              <div className={styles.info}>
                <p className={styles.name}>{d.name}</p>
                <p className={styles.address}>
                  {d.location?.address1} {d.location?.postalCode} {d.location?.city}
                </p>
                <p className={styles.status}>{d.customerStatus}</p>
              </div>
            </button>
          ))}
        </div>
        <button
          type="button"
          className={styles.cancel}
          onClick={() => setDuplicates(null)}
        >
          Retour
        </button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2>Créer un prospect</h2>
      <div className={styles.form}>
        <div className={styles.field}>
          <label>Nom *</label>
          <InputText
            name="name"
            control={control}
            rules={{ required: 'Le nom est obligatoire' }}
            placeholder="Nom du point de vente"
          />
          {(errors as any).name && <ErrorField message={(errors as any).name.message} />}
        </div>
        <div className={styles.field}>
          <label>SIRET *</label>
          <InputText
            name="siret"
            control={control}
            rules={{
              required: 'Le SIRET est obligatoire',
              minLength: { value: 14, message: 'Le SIRET doit contenir 14 caractères' },
              maxLength: { value: 14, message: 'Le SIRET doit contenir 14 caractères' },
            }}
            placeholder="14 caractères"
            maxlength={14}
          />
          {(errors as any).siret && <ErrorField message={(errors as any).siret.message} />}
        </div>
        {sirenValue && (
          <div className={styles.field}>
            <label>SIREN</label>
            <p className={styles.siren}>{sirenValue}</p>
          </div>
        )}
        <div className={styles.field}>
          <label>Adresse *</label>
          <InputText
            name="location.address1"
            control={control}
            rules={{ required: 'L\'adresse est obligatoire' }}
            placeholder="Adresse"
          />
          {(errors as any)?.location?.address1 && (
            <ErrorField message={(errors as any).location.address1.message} />
          )}
        </div>
        <div className={styles.row}>
          <div className={styles.field}>
            <label>Code postal *</label>
            <InputText
              name="location.postalCode"
              control={control}
              rules={{ required: 'Le code postal est obligatoire' }}
              placeholder="Code postal"
              maxlength={5}
            />
            {(errors as any)?.location?.postalCode && (
              <ErrorField message={(errors as any).location.postalCode.message} />
            )}
          </div>
          <div className={styles.field}>
            <label>Ville *</label>
            <InputText
              name="location.city"
              control={control}
              rules={{ required: 'La ville est obligatoire' }}
              placeholder="Ville"
            />
            {(errors as any)?.location?.city && (
              <ErrorField message={(errors as any).location.city.message} />
            )}
          </div>
        </div>
        <button
          type="button"
          className={styles.submit}
          onClick={handleSubmit(onSubmit)}
        >
          Créer ce prospect
        </button>
      </div>
    </div>
  );
};

export default CreateProspectModal;
